import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DAYS = [
  { value: "mon", label: "Segunda" },
  { value: "tue", label: "Terça" },
  { value: "wed", label: "Quarta" },
  { value: "thu", label: "Quinta" },
  { value: "fri", label: "Sexta" },
  { value: "sat", label: "Sábado" },
  { value: "sun", label: "Domingo" },
];

export default function AdminSettings() {
  const [form, setForm] = useState({
    sobre_video_url: "",
    hero_video_url: "",
    newsletter_auto_enabled: false,
    newsletter_auto_day: "mon",
    newsletter_auto_hour: 9,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setForm({
        sobre_video_url: r.data.sobre_video_url || "",
        hero_video_url: r.data.hero_video_url || "",
        newsletter_auto_enabled: !!r.data.newsletter_auto_enabled,
        newsletter_auto_day: r.data.newsletter_auto_day || "mon",
        newsletter_auto_hour: r.data.newsletter_auto_hour ?? 9,
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Definições guardadas.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm";
  const labelCls = "block text-xs uppercase tracking-widest text-stone-500 mb-2";

  return (
    <AdminLayout>
      <div data-testid="admin-settings">
        <h1 className="font-display text-3xl text-stone-900">Definições do site</h1>
        <p className="mt-2 text-stone-600">Vídeos globais e automação da newsletter.</p>

        {loading ? <div className="mt-8 text-stone-500">A carregar...</div> : (
          <div className="mt-8 space-y-6 max-w-3xl">
            <div className="card-soft p-6">
              <label className={labelCls}>Vídeo na página &ldquo;Sobre a Zélia&rdquo; (URL YouTube/Vimeo)</label>
              <input value={form.sobre_video_url} onChange={(e) => setForm({...form, sobre_video_url: e.target.value})} data-testid="settings-sobre-video" className={inputCls} placeholder="https://www.youtube.com/watch?v=..." />
              <p className="mt-2 text-xs text-stone-500">Deixe vazio para não mostrar vídeo.</p>
            </div>

            <div className="card-soft p-6">
              <label className={labelCls}>Vídeo no topo da homepage (opcional)</label>
              <input value={form.hero_video_url} onChange={(e) => setForm({...form, hero_video_url: e.target.value})} data-testid="settings-hero-video" className={inputCls} placeholder="https://..." />
            </div>

            <div className="card-soft p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-stone-900">Envio automático da newsletter</div>
                  <div className="text-sm text-stone-600 mt-1">Enviar o digest semanal automaticamente sem clicar em nada.</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.newsletter_auto_enabled}
                    onChange={(e) => setForm({...form, newsletter_auto_enabled: e.target.checked})}
                    data-testid="settings-auto-toggle"
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-stone-300 rounded-full peer peer-checked:bg-[#103C4A] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                </label>
              </div>
              {form.newsletter_auto_enabled && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Dia da semana</label>
                    <select value={form.newsletter_auto_day} onChange={(e) => setForm({...form, newsletter_auto_day: e.target.value})} data-testid="settings-auto-day" className={inputCls}>
                      {DAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Hora (Lisboa, 0–23)</label>
                    <input type="number" min="0" max="23" value={form.newsletter_auto_hour} onChange={(e) => setForm({...form, newsletter_auto_hour: Number(e.target.value)})} data-testid="settings-auto-hour" className={inputCls} />
                  </div>
                </div>
              )}
              <p className="mt-3 text-xs text-stone-500">
                Requer <code className="bg-stone-100 px-1 rounded">RESEND_API_KEY</code> definida em <code className="bg-stone-100 px-1 rounded">backend/.env</code>. Se não estiver definida, o envio automático não acontece mas nada quebra.
              </p>
            </div>

            <button onClick={save} disabled={saving} data-testid="settings-save" className="btn-primary">
              <Save className="h-4 w-4" /> {saving ? "A guardar..." : "Guardar"}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
